// react
import { useEffect } from 'react';
import { useJoyride } from 'react-joyride';

// tanstack
import { useLocation, useNavigate } from '@tanstack/react-router';

// material ui
import { alpha, useTheme } from '@mui/material/styles';

// relative
import AppTourTooltip from './AppTourTooltip';

/**
 * @param {{
 *  steps: import('react-joyride').Step[];
 *  styles?: object;
 *  tooltipComponent?: any;
 *  floaterProps?: object;
 *  options?: object;
 *  [key: string]: any;
 * }} params
 */
export default function useAppTour({
  steps,
  styles = {},
  tooltipComponent,
  floaterProps = {},
  options = {},
  ...props
} = {}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Keep all Joyride layers on the same z-index scale to avoid portal stacking mismatches.
  const tourZIndexBase = theme.zIndex.modal + 100;

  const joyride = useJoyride({
    continuous: true,
    steps,
    tooltipComponent: tooltipComponent || AppTourTooltip,
    hideOverlay: false,
    options: {
      textColor: theme.palette.text.primary,
      primaryColor: theme.palette.primary.main,
      overlayColor: alpha(
        theme.palette.common.black,
        theme.palette.mode === 'dark' ? 0.72 : 0.5,
      ),
      zIndex: tourZIndexBase,
      ...options,
    },
    floaterProps: {
      ...floaterProps,
    },
    styles: {
      arrow: {
        color: theme.palette.background.paper,
      },
      tooltip: {
        boxShadow: theme.shadows[8],
        backgroundColor: theme.palette.background.paper,
        borderRadius: 8,
        fontSize: 14,
      },
      tooltipContent: {
        color: theme.palette.text.primary,
        margin: 8,
        padding: 8,
      },
      tooltipContainer: {
        textAlign: 'left',
      },
      tooltipTitle: {
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: 18,
        padding: 8,
      },
      tooltipFooter: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        marginTop: 12,
      },
      buttonClose: {
        color: theme.palette.text.secondary,
      },
      ...styles,
    },
    ...props,
  });

  const { on } = joyride;

  useEffect(() => {
    const clearTourSearchParams = () => {
      navigate({
        to: location.pathname,
        search: (prev) => {
          if (!prev || typeof prev !== 'object') {
            return prev;
          }

          let changed = false;
          const next = { ...prev };

          Object.keys(next).forEach((key) => {
            const normalizedKey = key.toLowerCase();
            const isTourParam =
              normalizedKey === 'tour' ||
              normalizedKey === 'step' ||
              normalizedKey.startsWith('tour');

            if (isTourParam) {
              delete next[key];
              changed = true;
            }
          });

          return changed ? next : prev;
        },
        replace: true,
      });
    };

    const unsubStepAfter = on('step:after', (data, controls) => {
      if (data?.action === 'close') {
        controls.stop();
        controls.reset();
        clearTourSearchParams();
      }
    });

    const unsubTourEnd = on('tour:end', () => {
      clearTourSearchParams();
    });

    return () => {
      unsubStepAfter?.();
      unsubTourEnd?.();
    };
  }, [on, navigate, location.pathname]);

  return joyride;
}
